import { cloneDeep, omit } from "lodash";
import {
  type FormkitNode,
  ModifierType,
  NodeType,
  type OptionsOrReference,
} from "../../api";

/**
 * Returns a copy of a FormkitNode tree without validation properties for each component
 * so the tree can be properly serialized before being passed
 * to the frontend.
 */
export function getNodesWithoutValidations(
  nodes: FormkitNode[],
): FormkitNode[] {
  const clonedTree = cloneDeep(nodes);
  removeNodeValidations(clonedTree);
  return clonedTree;
}

/**
 * Recursively removes validation properties from a FormkitNode tree.
 * Note: operates in-place and directly changes the input object
 */
function removeNodeValidations(nodes: FormkitNode[]): void {
  for (const node of nodes) {
    if (node.type === NodeType.Component) {
      const { validation: _validation, ...propsWithoutValidation } = node.props;
      node.props = propsWithoutValidation;
    }

    removeNodeValidations(node.children ?? []);
  }
}

export type ResetScope = {
  /** State keys whose change triggers the reset. */
  keys: string[];
  /** Component keys the reset clears. */
  fields: string[];
};

function getComponentKeys(nodes: FormkitNode[]): string[] {
  return nodes.flatMap((node) => [
    ...(node.type === NodeType.Component ? [node.key] : []),
    ...getComponentKeys(node.children ?? []),
  ]);
}

/** Every ResetModifier in a FormkitNode tree, in document order. */
export function getResetScopes(nodes: FormkitNode[]): ResetScope[] {
  return nodes.flatMap((node) => [
    ...(node.type === NodeType.Modifier && node.modifier === ModifierType.Reset
      ? [{ keys: node.keys, fields: getComponentKeys(node.children) }]
      : []),
    ...getResetScopes(node.children ?? []),
  ]);
}

export function getZerothIndexAsDefault(props: {
  options: OptionsOrReference;
  default?: any;
  placeholder?: string;
}) {
  let defaultValue;
  let defaultPlaceholder;

  if (props.options instanceof Array && props.options.length > 0) {
    defaultValue = props.default || props.options[0]?.value;

    if (props.placeholder) {
      defaultPlaceholder = props.placeholder;
    } else {
      defaultPlaceholder = props.options[0]?.label ?? undefined;
    }
  }

  return { defaultValue, defaultPlaceholder };
}

export const maybeNested = (key: string, parent: string | undefined) => {
  return parent ? `${parent}.${key}` : key;
};

export function hasLiquid(value: unknown) {
  if (!value) return false;

  // Sometimes a nested object will be stringified with }} before {{ which causes the
  // condition below to fail, so we should check the values of an object individually
  if (
    typeof value === "object" &&
    Object.values(value).some((v) => typeof v === "object")
  ) {
    for (const v of Object.values(value)) {
      if (hasLiquid(v)) {
        return true;
      }
    }
  }

  const stringified = typeof value === "string" ? value : JSON.stringify(value);

  return (
    (stringified.includes("{{") &&
      stringified.includes("}}") &&
      stringified.indexOf("{{") < stringified.indexOf("}}")) ||
    (stringified.includes("{%") &&
      stringified.includes("%}") &&
      stringified.indexOf("{%") < stringified.indexOf("%}"))
  );
}

export type FormkitLiquidRenderer = {
  registerFilter(
    name: string,
    impl: (input: any, ...args: any[]) => unknown,
  ): void;
  parseAndRenderSync(template: string, context: object): string;
};

export function createFormkitRenderer<T extends FormkitLiquidRenderer>(
  engine: T,
): T {
  // Add filter to use a default value when input value is empty
  engine.registerFilter("default", (inputValue, defaultValue) => {
    return inputValue == null || inputValue === "" ? defaultValue : inputValue;
  });

  engine.registerFilter("omit", (input, ...keys) => {
    if (typeof input === "object" && input) {
      return omit(input, keys);
    }

    return input;
  });

  return engine;
}
